import { formatISO, isValid, parseISO } from 'date-fns';
/**
 * Converts a given numeric timestamp or ISO date string into a JavaScript `Date` object.
 *
 * If the input is a number, it is interpreted as a timestamp in milliseconds since the Unix epoch.
 * If the input is a string, it is parsed as an ISO 8601 date string.
 * If the input is neither a number nor a string, `undefined` is returned.
 *
 * @param {number | string} [value] - The value to convert into a Date. Can be:
 *  - A number (milliseconds since epoch)
 *  - A string (ISO 8601 date format)
 *  - Undefined or any other type (returns `undefined`).
 *
 * @returns {Date | undefined} A `Date` object representing the given value, or `undefined` if the value is invalid.
 */
export function convertValueToDate(value) {
    switch (typeof value) {
        case 'number':
            return new Date(value);
        case 'string':
            return parseISO(value);
        default:
            return undefined;
    }
}
/**
 * Converts a valid `Date` object into either a timestamp (milliseconds since Unix epoch)
 * or an ISO 8601 formatted string.
 *
 * If the date is invalid or `null`/`undefined`, the function returns `undefined`.
 *
 * @param {Date | null} [date] - The date to convert. Must be a valid `Date` instance.
 * @param {boolean} [isoString=false]
 *  - If `true`, returns an ISO 8601 string using `formatISO`.
 *  - If `false` or omitted, returns a numeric timestamp (milliseconds since epoch).
 *
 * @returns {number | string | undefined}
 *   - A numeric timestamp if `isoString` is `false`.
 *   - An ISO 8601 string if `isoString` is `true`.
 *   - `undefined` if the input date is invalid, `null`, or `undefined`.
 */
export function convertDateToValue(date, isoString) {
    if (date && isValid(date)) {
        return isoString ? formatISO(date) : date.getTime();
    }
    else {
        return undefined;
    }
}
